import { useState } from 'react';
import type { Connection, ProviderInfo } from '../types';
import { formatTimestamp } from '../lib/format';

interface Props {
  connections: Connection[];
  providers: ProviderInfo[];
  busyId: string | null;
  onAdd: () => void;
  onEdit: (connection: Connection) => void;
  onTest: (connection: Connection) => void;
  onSync: (connection: Connection) => void;
  onToggle: (connection: Connection) => void;
  onDelete: (connection: Connection) => void;
}

export function ConnectionsPanel({
  connections,
  providers,
  busyId,
  onAdd,
  onEdit,
  onTest,
  onSync,
  onToggle,
  onDelete,
}: Props) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const labelFor = (id: string) => providers.find((p) => p.id === id)?.label ?? id;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Connected systems</h2>
        <div className="spacer" />
        <button className="btn primary" onClick={onAdd}>
          + Connect a system
        </button>
      </div>
      <div className="card-body">
        {!connections.length ? (
          <div className="empty">
            <h3>No systems connected</h3>
            <p>Add Azure DevOps or Monday.com to start pulling in work assigned to you.</p>
          </div>
        ) : (
          <div className="conn-list">
            {connections.map((c) => {
              const busy = busyId === c.id;
              return (
                <div key={c.id} className={`conn ${c.enabled ? '' : 'disabled'}`}>
                  <div className="body">
                    <div className="name">
                      {c.name}
                      <span className="pill system-pill">{labelFor(c.provider)}</span>
                      {!c.enabled && <span className="pill todo">Disabled</span>}
                      {c.lastSyncStatus === 'error' && <span className="pill blocked">Last sync failed</span>}
                      {c.lastSyncStatus === 'ok' && <span className="pill done">Healthy</span>}
                    </div>
                    <div className="sub">
                      Last synced {formatTimestamp(c.lastSyncAt)}
                      {Object.entries(c.config).length > 0 && (
                        <>
                          {' · '}
                          <span className="kv">
                            {Object.entries(c.config)
                              .slice(0, 3)
                              .map(([k, v]) => `${k}=${v}`)
                              .join('  ')}
                          </span>
                        </>
                      )}
                    </div>
                    {c.lastSyncError && <div className="notice error" style={{ marginTop: 8 }}>{c.lastSyncError}</div>}
                    {confirmId === c.id && (
                      <div className="notice warn" style={{ marginTop: 8 }}>
                        <div>
                          Remove “{c.name}”? Its synced tasks will be deleted from Z3NTRAFY. Nothing is deleted in{' '}
                          {labelFor(c.provider)}.
                          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                            <button className="btn danger sm" onClick={() => onDelete(c)}>
                              Yes, remove
                            </button>
                            <button className="btn sm" onClick={() => setConfirmId(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="actions">
                    <button className="btn sm" onClick={() => onSync(c)} disabled={busy || !c.enabled}>
                      {busy && <span className="spin" />}
                      Sync
                    </button>
                    <button className="btn sm" onClick={() => onTest(c)} disabled={busy}>
                      Test
                    </button>
                    <button className="btn sm" onClick={() => onEdit(c)} disabled={busy}>
                      Edit
                    </button>
                    <button className="btn sm" onClick={() => onToggle(c)} disabled={busy}>
                      {c.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button className="btn sm danger" onClick={() => setConfirmId(c.id)} disabled={busy}>
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
